import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChild,
    forwardRef,
    inject,
    Inject,
    Optional,
    Signal,
    signal,
    SkipSelf,
    TemplateRef,
    viewChild} from "@angular/core";
import { FormWrapper, VContainerComponent } from "../wrapper.interface";
import { FileUploadConfig } from "./fileUpload.config";
import { ControlContainer, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { V_COMPONENT_CONTAINER, V_FORM_CONTROL_TOKEN, WRAPPER_TOKEN } from "../wrapper.tokens";
import { AppService } from "src/services/app.service";
import { TranslateModule } from "@ngx-translate/core";
import { SelectedUploadFile, UploadFile } from "./interfaces";
import { GcLayoutModule } from "src/app/components/v-layout/layout.module";
import { VFileUploadListComponent } from "../fileUpload-list/fileUpload-list.component";
import { VFileUploadPickerComponent } from "../fileUpload-picker/fileUpload-picker.component";
import { v4 as uuid } from 'uuid';
import { DraftService } from "src/app/modules/ngx-draft";
import { FileUploadInfo } from "@vierkant-software/remoterpc";
import { FileType } from "@vierkant-software/types__api";

/**
 * TODO: @Richard
 * - Add template for card list container
 * - Add template for card
 */
@Component({
    selector:    'v-fileUpload',
    templateUrl: './fileUpload.component.haml',
    styleUrls:   ['./fileUpload.component.sass', '../input.style.sass'],
    providers:   [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => VFileUploadComponent) },
        { provide: WRAPPER_TOKEN, useExisting: forwardRef(() => VFileUploadComponent) },
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VFileUploadComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:         [CommonModule, ReactiveFormsModule, TranslateModule, GcLayoutModule, VFileUploadListComponent, VFileUploadPickerComponent]
})
export class VFileUploadComponent<T extends UploadFile>  extends FormWrapper<FileUploadConfig, string | string[]> {
    protected readonly _baseConfig: FileUploadConfig = FileUploadConfig.baseTheme.Default;
    protected readonly app = inject(AppService);
    public selectedFiles = signal<SelectedUploadFile[]>([]);

    protected listRef = viewChild.required<VFileUploadListComponent<T>>('list');
    protected uploadRef = viewChild.required<VFileUploadPickerComponent<T>>('picker');


    public emptyTemplateRef: Signal<TemplateRef<unknown>> = contentChild("empty");
    public previewTemplateRef: Signal<TemplateRef<unknown>> = contentChild("preview");
    public detailTemplateRef: Signal<TemplateRef<unknown>> = contentChild("detail");
    public isSingleMode = computed(() => this.config().upload.uploadLimit === 1);

    private get isDraft() {
        return this.draftService.isDraft;
    }

    constructor(
        private readonly draftService: DraftService,
        @Optional() protected controlContainer: ControlContainer,
        @Inject(V_COMPONENT_CONTAINER) @Optional() @SkipSelf() protected parent?: VContainerComponent,
    ){
        super(controlContainer, parent);
    }

    async addFile(file: SelectedUploadFile) {
        if (this.isDraft)await this.draftService.save(true);

        if (file.isDraft === undefined) {
            let fileUpload: FileUploadInfo = {};
            fileUpload[0] = await fetch(file.src.toString()).then(res => res.blob())
                .then(async res => ({ data: await res.arrayBuffer(), ContentType: res.type }));

            const uploadedFileID = this.isDraft ? await this.draftService.UploadDraftFile(fileUpload) : uuid();
            file.ID = uploadedFileID;
            file.isDraft = this.isDraft;
        }

        const limit = this.config().upload.uploadLimit;
        if (limit === 1) {
            this.selectedFiles.set([file]);
            await this.writeValue(file.ID);
        } else {
            if (limit >= 0 && this.selectedFiles().length >= limit) return;
            const files = this.selectedFiles();
            files.push(file);
            this.selectedFiles.set([...files]);
            await this.writeValue(this.selectedFiles().map(f => f.ID));
        }
    }

    async filesChanged(files: SelectedUploadFile[]) {
        const fileIDs = files.map(f => f.ID);
        const limit = this.config().upload.uploadLimit;
        await this.writeValue(limit === 1 ? (fileIDs.at(0) ?? null) : fileIDs);

        const filesToRemove = this.selectedFiles().filter(f => !fileIDs.includes(f.ID));
        this.selectedFiles.set([...files]);

        if (!filesToRemove.length) return;

        if (this.isDraft) {
            await Promise.all(filesToRemove.map(async (f) => {
                return await this.draftService.RemoveDraftFile(f.ID);
            }));
        }
    }

    async writeValue(value: string | string[]){
        if (!this.draftService.isDraft) return super.writeValue(value);

        // Restore files from draft
        const isArray = Array.isArray(value);
        const selectedFiles = this.selectedFiles().map(x => x.ID);
        const filesToFetch = (isArray ? value : [value]).filter(f => !!f && !selectedFiles.includes(f));
        const files = await this.draftService.GetFiles(filesToFetch);
        const fetchedFiles = filesToFetch.filter(x => !!x).map(x => {
            const file = files[x];
            return {
                ID:      file.ID,
                name:    "",
                src:     file.url,
                size:    file.fileSize,
                isDraft: file.type === FileType.DraftFile
            } as SelectedUploadFile;
        });
        const newValues = [...this.selectedFiles().filter(x => isArray ? value.includes(x.ID) : value === x.ID), ...fetchedFiles];
        this.selectedFiles.set(newValues);
        super.writeValue(value);
    }
}